import {
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  Save as SaveIcon,
  Close as CancelIcon,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import React, { useState } from "react";
import { WorkItemList } from "../../store/services/timelineApiRTK";

interface CustomerInfoCardProps {
  activity: WorkItemList;
  onCustomerClick: () => void;
  onUpdateDescription: (description: string) => Promise<void>;
}

const CustomerInfoCard: React.FC<CustomerInfoCardProps> = ({
  activity,
  onCustomerClick,
  onUpdateDescription,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newDescription, setNewDescription] = useState(activity.Description || "");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleEditClick = () => {
    setIsEditing(true);
    setNewDescription(activity.Description || "");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewDescription(activity.Description || "");
  };

  const handleSaveDescription = async () => {
    setIsUpdating(true);
    try {
      await onUpdateDescription(newDescription);
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating description:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box>
      {/* Customer row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="body2" fontWeight={600}>
          {activity.Customer?.Name || "Ingen kund"}
        </Typography>
        {activity.Customer?.CustomerId && (
          <Typography variant="caption" color="text.secondary">
            #{activity.Customer.CustomerId}
          </Typography>
        )}
        {activity.Customer?.Id && (
          <Tooltip title="Gå till kundprofil">
            <IconButton
              size="small"
              onClick={onCustomerClick}
              sx={{ ml: "auto", color: "text.secondary", "&:hover": { color: "primary.main" } }}
            >
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Description */}
      {isEditing ? (
        <Box>
          <TextField
            multiline
            rows={4}
            fullWidth
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Skriv en beskrivning..."
            variant="outlined"
            size="small"
            disabled={isUpdating}
            autoFocus
          />
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", mt: 1 }}>
            <IconButton size="small" onClick={handleCancelEdit} disabled={isUpdating}>
              <CancelIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleSaveDescription}
              disabled={isUpdating}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
                "&:disabled": { bgcolor: "grey.300" },
              }}
            >
              {isUpdating ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box
          onClick={handleEditClick}
          sx={{
            p: 1.5,
            bgcolor: "grey.50",
            borderRadius: 1.5,
            cursor: "pointer",
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              whiteSpace: "pre-wrap",
              fontStyle: activity.Description ? "normal" : "italic",
              color: activity.Description ? "text.primary" : "text.secondary",
            }}
          >
            {activity.Description || "Ingen beskrivning — klicka för att lägga till"}
          </Typography>
          <EditIcon sx={{ fontSize: 14, color: "text.disabled", mt: 0.25 }} />
        </Box>
      )}
    </Box>
  );
};

export default CustomerInfoCard;
